import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, HumiLab } from 'components/UIElements'
import { formatAndSanitizeHTML, convertImageToSupportedFormat } from 'utils/misc'
import HumiIsabelaOnboarding from './HumiIsabelaOnboarding'
import Signature from './Signature'
import Input from 'components/Input'
import {
  allSignaturesComplete,
  arePreSignatureComprehensionQuestionsSatisfied,
  formatSubmissionConsent,
  getPreSignatureFlowSections,
  getPreSignatureComprehensionSections,
  getSectionsArr,
  setConsentClosedToLocalStorage
} from '../utils/consentUtils'
import '../stylesheets/consent.scss'
import PasswordChangeSuccessView from './PasswordChangeSuccessView'
import ConsentIntroductionView from './ConsentIntroductionView'
import ConsentInProgressView from './ConsentInProgressView'
import ConsentCompletedView from './ConsentCompletedView'
import ConsentAwaitingAdditionalSignaturesView from './ConsentAwaitingAdditionalSignaturesView'
import ConsentComprehension from './ConsentComprehension'
import ConsentPdfViewer from './ConsentPdfViewer'
import { navigateTo } from 'navigation'
import { CONSENT_PARTICIPANT_STATUSES, CONSENT_SECTION_TYPES, USER_TYPES, CAREGIVER_TEXT, PARTICIPANT_TEXT, CONSENT_SIGNATURE_MODES, LOCAL_STORAGE_KEYS, FALLBACK_STRINGS } from 'utils/constants'
import { logSentryEvent, SENTRY_MESSAGES } from '../../../utils/sentry'

const ConsentPage = props => {
  const {
    clearConsentStatus,
    closeModal,
    changedTempPassword,
    completeConsentQuestion,
    consentForm,
    v2Consents,
    downloadConsentPDF,
    endConsent,
    error,
    firstName,
    onConsentOpen,
    onConsentReject,
    hasConsented,
    onLogout,
    onSetSubmissionConsent,
    openModal,
    skipConsentQuestion,
    strings,
    submissionConsent,
    submitConsent,
    studyID,
    humiIsabellaDisabled,
    readOnlyConsent = false,
    dispatch
  } = props

  const [didStartConsent, setDidStartConsent] = useState(false)
  const [scrollTop, setScrollTop] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const contentRef = useRef(null)
  const consentOpenEventSent = useRef(false)

  const isBlockingConsent = consentForm?.is_blocking || false
  const consentName = consentForm?.consent_title
  const consentId = consentForm?.consent_id
  const consentVersion = consentForm?.consent_version || consentForm?.last_deployed_version
  // Extract signature options from the signature section attributes
  const getSignatureOptions = (section) => {
    if (section?.type !== CONSENT_SECTION_TYPES.signature) return [CONSENT_SIGNATURE_MODES.draw]

    const attrs = section?.attributes || {}

    const options = []
    if (attrs.signature_draw) options.push(CONSENT_SIGNATURE_MODES.draw)
    if (attrs.signature_and_camera_toggle) options.push(CONSENT_SIGNATURE_MODES.photoUpload)

    return options.length > 0 ? options : [CONSENT_SIGNATURE_MODES.draw]
  }

  const activeConsent = v2Consents?.find(
    v2Consent => v2Consent.consent_id === consentForm?.consent_id
  )

  // useEffect(() => {
  //   if (consentForm && !Object.keys(consentForm)?.length) {
  //     // navigateTo('/profile')
  //   }
  // }, [])

  useEffect(() => {
    // Only call onConsentOpen once when consent data is available
    if (!consentOpenEventSent.current) {
      let id, version

      // First try to get from consentForm (could be object or array)
      if (consentForm) {
        if (Array.isArray(consentForm) && consentForm.length > 0) {
          const firstConsent = consentForm[0]
          id = firstConsent?.consent_id
          version = firstConsent?.consent_version || firstConsent?.last_deployed_version
        } else if (Object.keys(consentForm).length > 0) {
          id = consentForm?.consent_id
          version = consentForm?.consent_version || consentForm?.last_deployed_version
        }
      }

      // If not available in consentForm, try v2Consents
      if ((!id || !version) && v2Consents && v2Consents.length > 0) {
        const firstConsent = v2Consents[0]
        id = id || firstConsent?.consent_id
        version = version || firstConsent?.last_deployed_version
      }

      if (id !== undefined && id !== null && version !== undefined && version !== null) {
        onConsentOpen(studyID, id, version, consentName)
        consentOpenEventSent.current = true
      } else if ((consentForm && (Array.isArray(consentForm) ? consentForm.length > 0 : Object.keys(consentForm).length > 0)) || (v2Consents && v2Consents.length > 0)) {
        // If consent data is loaded but missing id/version, still call the event
        onConsentOpen(studyID)
        consentOpenEventSent.current = true
      }
    }
  }, [consentForm, v2Consents, studyID])

  // Only initialize submission when loading a different consent or when none exists.
  // Do not overwrite when we already have a submission for this consent (e.g. user has drawn
  // signature) — that would wipe their input and disable I Agree (button flickered).
  useEffect(() => {
    if (!consentForm || !consentForm.consent_id) return
    const formVersion = consentForm.consent_version || consentForm.last_deployed_version
    const submissionVersion = submissionConsent?.version
    const sameConsent =
      submissionConsent?.consent_id === consentForm.consent_id &&
      submissionVersion === formVersion
    const hasExistingSubmission =
      sameConsent &&
      Array.isArray(submissionConsent?.questions) &&
      submissionConsent.questions.length > 0
    if (hasExistingSubmission) return
    const formattedConsent = formatSubmissionConsent(consentForm)
    onSetSubmissionConsent(formattedConsent)
    return () => clearConsentStatus()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only init when consent identity or empty submission; consentForm ref changes must not overwrite user's signature
  }, [consentForm?.consent_id, consentForm?.consent_version, consentForm?.last_deployed_version, submissionConsent?.consent_id, submissionConsent?.version, submissionConsent?.questions?.length])

  const onScroll = () => {
    const newScrollTop = contentRef?.current?.scrollTop
    setScrollTop(newScrollTop)
  }

  const onStartConsent = () => {
    logSentryEvent({
      message: SENTRY_MESSAGES.consentOpened,
      tags: {
        flow: 'consent',
        action: 'started',
        consent_id: consentId != null ? String(consentId) : undefined,
        consent_name: consentName,
        consent_version: consentVersion != null ? String(consentVersion) : undefined,
      },
    })
    setDidStartConsent(true)
    if (preSignatureFlowSections.length > 0 && !preSignatureComprehensionComplete) {
      setShowPreSignatureComprehension(true)
    }
  }
  const onSignEnd = (id, signatureData) => {
    completeConsentQuestion(id, signatureData)
  }

  const normalizeSignatureEntry = (data) => {
    if (!data) return null
    if (typeof data === 'string') {
      return { value: data, signature_type: CONSENT_SIGNATURE_MODES.draw }
    }
    if (typeof data === 'object' && (data.value || data.value === '')) {
      // assumes data.signature_type already present (e.g., photo_upload)
      const entry = { value: data.value, signature_type: data.signature_type || CONSENT_SIGNATURE_MODES.draw }
      if (data.file_name) entry.file_name = data.file_name
      if (data.mime_type) entry.mime_type = data.mime_type
      return entry
    }
    return null
  }

  const onCaregiverSignatureUpdate = (sectionId, data) => {
    setCaregiverSignature(data)
    const signatureEntry = normalizeSignatureEntry(data)
    const payload = []
    // Include caregiver name when provided (mirror participant answer shape)
    const nameForSection = caregiverNames?.[sectionId]
    if (nameForSection && nameForSection.length > 0) {
      payload.push({ value: nameForSection, signature_type: CONSENT_SIGNATURE_MODES.text })
    }
    if (signatureEntry) {
      payload.push(signatureEntry)
    }
    completeConsentQuestion(sectionId, payload)
  }

  const onParticipantSignatureUpdate = (sectionId, data) => {
    setParticipantSignature(data)
    const signatureEntry = normalizeSignatureEntry(data)
    const payload = []
    // Include participant name only if that section has name input enabled
    const nameForSection = participantNames?.[sectionId]
    if (nameInputEnabledSectionIds.has(sectionId) && nameForSection && nameForSection.length > 0) {
      payload.push({ value: nameForSection, signature_type: CONSENT_SIGNATURE_MODES.text })
    }
    if (signatureEntry) {
      payload.push(signatureEntry)
    }
    completeConsentQuestion(sectionId, payload)
  }
  const toBlobFromBase64 = (base64, mimeType = 'image/png') => {
    const byteChars = atob(base64)
    const byteNumbers = new Array(byteChars.length)
    for (let i = 0; i < byteChars.length; i += 1) byteNumbers[i] = byteChars.charCodeAt(i)
    const byteArray = new Uint8Array(byteNumbers)
    return new Blob([byteArray], { type: mimeType })
  }

  const performFileUploads = async (submission) => {
    const updated = JSON.parse(JSON.stringify(submission))
    const tasks = []

    // Check if there are any files to upload (signature uploads or PDF uploads)
    const hasFilesToUpload = (() => {
      if (!Array.isArray(updated?.questions)) return false
      return updated.questions.some(q =>
        Array.isArray(q?.answer) && q.answer.some(entry =>
          entry && (entry.signature_type === CONSENT_SIGNATURE_MODES.photoUpload ||
            entry.signature_type === CONSENT_SIGNATURE_MODES.image ||
            entry.signature_type === CONSENT_SIGNATURE_MODES.pdf) && entry.value
        )
      )
    })()


    if (!hasFilesToUpload) {
      return updated
    }
    // Locate the first PDF question in the submission to populate its answer (if it exists)
    const pdfQuestionIndex = Array.isArray(updated?.questions)
      ? updated.questions.findIndex(q => q?.type === CONSENT_SECTION_TYPES.pdf)
      : -1
    updated?.questions?.forEach((q, qIdx) => {
      if (!Array.isArray(q?.answer)) return
      q.answer.forEach((entry, aIdx) => {
        // Handle both image and PDF uploads
        if (entry && (entry.signature_type === CONSENT_SIGNATURE_MODES.photoUpload ||
          entry.signature_type === CONSENT_SIGNATURE_MODES.image ||
          entry.signature_type === CONSENT_SIGNATURE_MODES.pdf) && entry.value) {
          const base64 = typeof entry.value === 'string' ? entry.value : ''
          if (!base64) return

          // Use actual MIME type from file (avoids "image/jpeg with extension png" mismatch)
          const mimeToExt = {
            'image/jpeg': 'jpg',
            'image/jpg': 'jpg',
            'image/png': 'png',
            'application/pdf': 'pdf'
          }
          const extFromMime = (mime) => mimeToExt[mime?.toLowerCase()] || 'png'
          const baseName = (entry.file_name && entry.file_name.includes('.'))
            ? entry.file_name.replace(/\.[^.]+$/, '')
            : `consent_${q?.id || 'signature'}_${Date.now()}`

          const task = (async () => {
            let mimeType = entry.mime_type && (entry.mime_type.startsWith('image/') || entry.mime_type === 'application/pdf')
              ? entry.mime_type
              : entry.signature_type === CONSENT_SIGNATURE_MODES.pdf
                ? 'application/pdf'
                : 'image/png'

            let base64ToUse = base64
            if (mimeType !== 'application/pdf') {
              const converted = await convertImageToSupportedFormat(base64, mimeType)
              base64ToUse = converted.base64
              mimeType = converted.mimeType
            }

            const ext = extFromMime(mimeType)
            const fileName = `${baseName}.${ext}`
            const formData = new FormData()
            formData.set('upload_file', toBlobFromBase64(base64ToUse, mimeType), fileName)
            formData.set('file_name', fileName)
            formData.set('module_name', 'consent')
            return props.uploadConsentFile(formData).then((res) => {
              if (res?.message !== 'SUCCESS') {
                throw new Error(res?.data?.internal_message || 'File upload failed')
              }
              const isPdfFile = mimeType === 'application/pdf'
              // Populate signature entry referencing uploaded file
              updated.questions[qIdx].answer[aIdx] = {
                value: fileName,
                signature_type: isPdfFile ? 'pdf' : 'image',
                label: isPdfFile ? 'Signed PDF (uploaded)' : 'Signed Image (uploaded)'
              }
              // Also populate the PDF question answer payload
              if (pdfQuestionIndex > -1) {
                updated.questions[pdfQuestionIndex] = Object.assign({}, updated.questions[pdfQuestionIndex], {
                  answer: [
                    {
                      value: fileName,
                      filename: fileName,
                      mimetype: mimeType,
                      label: isPdfFile ? 'Signed consent PDF' : 'Signed consent Image'
                    }
                  ]
                })
              }
              return true
            })
          })()
          tasks.push(task)
        }
      })
    })
    await Promise.all(tasks)
    return updated
  }

  const submitWithUploads = async () => {
    setIsSubmitting(true)
    try {
      logSentryEvent({
        message: SENTRY_MESSAGES.consentStartSubmit,
        tags: {
          flow: 'consent',
          action: 'submit_started',
          consent_id: consentId != null ? String(consentId) : undefined,
          consent_name: consentName,
          consent_version: consentVersion != null ? String(consentVersion) : undefined,
        },
      })
      const withUploads = await performFileUploads(submissionConsent)
      await submitConsent(withUploads, studyID, strings.consent_error)
      logSentryEvent({
        message: SENTRY_MESSAGES.consentSubmitted,
        tags: {
          flow: 'consent',
          action: 'submitted',
          consent_id: consentId != null ? String(consentId) : undefined,
          consent_name: consentName,
          consent_version: consentVersion != null ? String(consentVersion) : undefined,
        },
      })
    } catch (error) {
      logSentryEvent({
        message: 'Consent Submit Failed',
        level: 'error',
        tags: {
          flow: 'consent',
          action: 'submit_failed',
          consent_id: consentId != null ? String(consentId) : undefined,
          consent_name: consentName,
          consent_version: consentVersion != null ? String(consentVersion) : undefined,
        },
        error
      })
      throw error
    } finally {
      setIsSubmitting(false)
    }
  }

  const onConsent = async () => {
    // Prevent multiple submissions
    if (isSubmitting) {
      return
    }
    // If there are post-sign sections (additional text / comprehension), show them first.
    const postSignSections = getPostSignSections()
    if (postSignSections.length > 0) {
      setShowComprehension(true)
      return
    }
    await submitWithUploads()
  }

  const onConsentDecline = () => {
    openModal({
      className: 'consent-quit',
      heading: strings.consent_decline_heading || FALLBACK_STRINGS.consent_decline_heading,
      content: isBlockingConsent
        ? strings.consent_decline_content_blocking || FALLBACK_STRINGS.consent_decline_content_blocking
        : strings.consent_decline_content_non_blocking || FALLBACK_STRINGS.consent_decline_content_non_blocking,
      confirmButton: strings.consent_decline_confirm_button || FALLBACK_STRINGS.consent_decline_confirm_button,
      cancelButton: strings.return_button || FALLBACK_STRINGS.return_button,
      closeOnBackgroundClick: true,
      humi: !humiIsabellaDisabled,
      humi: false,
      onConfirm: () => {
        logSentryEvent({
          message: SENTRY_MESSAGES.consentRejected,
          tags: {
            flow: 'consent',
            action: 'declined',
            consent_id: consentId != null ? String(consentId) : undefined,
            consent_name: consentName,
            consent_version: consentVersion != null ? String(consentVersion) : undefined,
          },
        })
        onConsentReject(studyID, consentId, consentVersion)
        if (isBlockingConsent) {
          onLogout()
        } else {
          setConsentClosedToLocalStorage()
          navigateTo('/profile')
        }
      }
    })
  }

  const onConsentClose = () => {
    openModal({
      className: 'consent-cancel',
      heading: strings.consent_skip_heading || FALLBACK_STRINGS.consent_skip_heading,
      content: strings.consent_skip_content || FALLBACK_STRINGS.consent_skip_content,
      confirmButton: strings.skip || FALLBACK_STRINGS.skip,
      cancelButton: strings.return_button || FALLBACK_STRINGS.return_button,
      closeOnBackgroundClick: true,
      humi: false,
      onConfirm: () => {
        logSentryEvent({
          message: 'Consent flow: skipped',
          tags: {
            flow: 'consent',
            'consent.action': 'skipped'
          },
          contexts: {
            consent: {
              id: consentId,
              version: consentVersion,
              isBlocking: isBlockingConsent
            }
          }
        })
        setConsentClosedToLocalStorage()
        navigateTo('/profile')
      }
    })
  }

  const onConsentRead = () => {
    openModal({
      className: 'consent-read',
      heading: strings.consent_read_heading || FALLBACK_STRINGS.consent_read_heading,
      content: strings.consent_read_content || FALLBACK_STRINGS.consent_read_content,
      confirmButton: strings.consent_read_confirm_button || FALLBACK_STRINGS.consent_read_confirm_button,
      cancelButton: strings.return_button || FALLBACK_STRINGS.return_button,
      closeOnBackgroundClick: true,
      humi: false,
      onConfirm: () => {
        onConsent()
      }
    })
  }

  const onNextButtonClick = () => {
    contentRef?.current?.scrollBy({ top: 500, behavior: 'smooth' })
  }
  const onBackButtonClick = () => {
    contentRef?.current?.scrollBy({ top: -500, behavior: 'smooth' })
  }

  const onOnboard = () => {
    endConsent()

    if (!humiIsabellaDisabled) {
      openModal({
        className: 'onboarding-modal',
        content: <HumiIsabelaOnboarding strings={strings} />,
        closeOnBackgroundClick: true,
        confirmButton: strings.linkt_intro_confirm,
        onConfirm: () => {
          closeModal()
          navigateTo('/profile')
        }
      })
    } else {
      navigateTo('/profile')
    }
  }

  const sections = getSectionsArr(consentForm.questions, consentForm.order)
  const pdfUrlFromQuestions = Array.isArray(sections)
    ? (sections.find(s => s?.type === CONSENT_SECTION_TYPES.pdf)?.url || null)
    : null

  // Get prompt text from the first prompt section
  const getPromptText = () => {
    const promptSection = sections.find(s => s?.type === CONSENT_SECTION_TYPES.prompt)
    return promptSection?.label || consentName || 'Consent Form'
  }

  // Resolve default participant full name from localStorage user JSON when available
  const getDefaultParticipantFullName = () => {
    try {
      const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.user) || localStorage.getItem('user')
      if (!raw) return ''
      const u = JSON.parse(raw) || {}
      const first = u.fname || ''
      const last = u.lname || ''
      if (first || last) {
        // Display as "First Last" per requirement examples
        return [first, last].filter(Boolean).join(' ')
      }
      return ''
    } catch (_) {
      return ''
    }
  }

  // Determines whether to show the signature mode toggle for a given section
  const shouldShowSignatureToggle = (section) => {
    if (section?.type !== CONSENT_SECTION_TYPES.signature) return false

    const attrs = section?.attributes || {}
    // Only show toggle if signature_draw is true AND signature_and_camera_toggle is true
    return attrs.signature_draw && attrs.signature_and_camera_toggle
  }

  const getPostSignSections = () => {
    // Prefer explicit admin-configured list if provided
    const fromMeta = consentForm?.metadata?.post_sign_section_ids
    if (Array.isArray(fromMeta) && fromMeta.length) {
      return fromMeta
        .map(id => ({ id, ...consentForm?.questions?.[id] }))
        .filter(section => section && section.id)
    }
    // Fallback: everything after the last signature in ordered list
    const sigIndexes = sections
      .map((section, index) => (section.type === CONSENT_SECTION_TYPES.signature ? index : -1))
      .filter(index => index > -1)
    if (!sigIndexes.length) return []
    const lastSigIdx = Math.max(...sigIndexes)
    return sections.slice(lastSigIdx + 1).filter(s => s && s.id)
  }
  const [showComprehension, setShowComprehension] = useState(false)
  const [showPreSignatureComprehension, setShowPreSignatureComprehension] = useState(false)
  const [preSignatureComprehensionComplete, setPreSignatureComprehensionComplete] = useState(false)

  const [caregiverNames, setCaregiverNames] = useState({})
  const [caregiverSignature, setCaregiverSignature] = useState('')
  const [participantNames, setParticipantNames] = useState({})
  const [participantSignature, setParticipantSignature] = useState('')
  const hasCaregiver = Array.isArray(consentForm?.metadata?.user_type_deployment_order)
    ? consentForm.metadata.user_type_deployment_order.includes(USER_TYPES.caregiver)
    : false

  const preSignatureFlowSections = getPreSignatureFlowSections(sections, hasCaregiver)
  const preSignatureComprehensionSections = getPreSignatureComprehensionSections(sections, hasCaregiver)

  useEffect(() => {
    // If there are no pre-sign sections, mark complete and never show the flow.
    if (preSignatureFlowSections.length === 0) {
      setPreSignatureComprehensionComplete(true)
      setShowPreSignatureComprehension(false)
    } else {
      // Consent changed: reset gating so users must complete the pre-sign flow again.
      setPreSignatureComprehensionComplete(false)
      setShowPreSignatureComprehension(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derived sections change when consent changes
  }, [consentForm?.consent_id, consentForm?.consent_version, consentForm?.last_deployed_version])

  // Determine the last renderable signature section index based on whether caregiver is present
  const NO_MATCH_INDEX = -1
  const lastRenderableSignatureIdx = Array.isArray(sections)
    ? sections.reduce((lastIdx, section, idx) => {
      if (section?.type !== CONSENT_SECTION_TYPES.signature) return lastIdx
      const userType = section?.attributes?.user_type
      const isRenderable =
        userType === USER_TYPES.participant || (userType === USER_TYPES.caregiver && hasCaregiver)
      return isRenderable ? idx : lastIdx
    }, NO_MATCH_INDEX)
    : NO_MATCH_INDEX

  // Precompute which signature sections have name inputs enabled to avoid repeated scans
  const nameInputEnabledSectionIds = new Set(
    Array.isArray(sections)
      ? sections
        .filter(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.include_name_input)
        .map(s => s.id)
      : []
  )

  const renderContent = () => {
    return sections.map((section, idx) => {
      if (section.type === CONSENT_SECTION_TYPES.prompt) {
        // Prompt/introduction is presented in the pre-sign one-question-per-screen flow.
        // Avoid showing it again when the user reaches the signature screen.
        if (preSignatureComprehensionComplete && preSignatureFlowSections.length > 0) {
          return null
        }
        return (
          <p
            key={section.id}
            dangerouslySetInnerHTML={{
              __html: formatAndSanitizeHTML(section.label)
            }}
          />
        )
      }
      if (section.type === CONSENT_SECTION_TYPES.pdf && section.url) {
        // If PDF was already shown in the pre-sign flow (per payload order), don't show it again.
        if (
          preSignatureComprehensionComplete &&
          preSignatureFlowSections.some(s => s?.id === section.id && s?.type === CONSENT_SECTION_TYPES.pdf)
        ) {
          return null
        }
        return (
          <div key={section.id} className='consent-section consent-pdf-viewer'>
            <ConsentPdfViewer
              url={section.url}
              title={section.label || consentName || 'Consent PDF'}
            />
          </div>
        )
      }
      if (section.type === CONSENT_SECTION_TYPES.signature) {
        if (section?.attributes?.user_type === USER_TYPES.participant) {
          return (
            <div key={section.id} className='consent-section participant'>
              <h4><strong>{PARTICIPANT_TEXT.heading}</strong></h4>
              <p>{strings.participant_signature_prompt}</p>
              {nameInputEnabledSectionIds.has(section.id) && (
                <Input
                  placeholder={strings.participant_enter_full_name_placeholder}
                  value={participantNames?.[section.id] || ''}
                  onChange={val => setParticipantNames(prev => ({ ...prev, [section.id]: val }))}
                  inputClassName='custom-input'
                />
              )}
              <Signature
                canSkip={section?.attributes?.required === false}
                fullName={participantNames?.[section.id] || getDefaultParticipantFullName()}
                promptText={getPromptText()}
                questionId={section.id}
                isFinalSection={idx === lastRenderableSignatureIdx}
                signatureOptions={getSignatureOptions(section)}
                showToggle={shouldShowSignatureToggle(section)}
                consentName={consentName}
                consentVersion={consentVersion}
                pdfUrl={pdfUrlFromQuestions}
                printToSign={section?.attributes?.print_to_sign}
                consentId={consentId}
                dispatch={dispatch}
                openModal={openModal}
                skipConsentQuestion={skipConsentQuestion}
                strings={strings}
                onSignUpdate={(data) => onParticipantSignatureUpdate(section.id, data)}
                isCaregiverSignature={false}
              />
            </div>
          )
        }
        if (section?.attributes?.user_type === USER_TYPES.caregiver && hasCaregiver) {
          return (
            <div key={section.id} className='consent-section caregiver'>
              <h4><strong>{CAREGIVER_TEXT.heading}</strong></h4>
              <p>{strings.caregiver_signature_prompt}</p>
              {nameInputEnabledSectionIds.has(section.id) && (
                <Input
                  placeholder={strings.caregiver_enter_full_name_placeholder}
                  value={caregiverNames?.[section.id] || ''}
                  onChange={val => setCaregiverNames(prev => ({ ...prev, [section.id]: val }))}
                  inputClassName='custom-input'
                />
              )}
              <Signature
                canSkip={section?.attributes?.required === false}
                fullName={caregiverNames?.[section.id] || ''}
                promptText={getPromptText()}
                questionId={section.id}
                isFinalSection={idx === lastRenderableSignatureIdx}
                signatureOptions={getSignatureOptions(section)}
                showToggle={shouldShowSignatureToggle(section)}
                consentName={consentName}
                consentVersion={consentVersion}
                pdfUrl={pdfUrlFromQuestions}
                printToSign={section?.attributes?.print_to_sign}
                consentId={consentId}
                dispatch={dispatch}
                openModal={openModal}
                skipConsentQuestion={skipConsentQuestion}
                strings={strings}
                onSignUpdate={(data) => onCaregiverSignatureUpdate(section.id, data)}
                isCaregiverSignature={true}
              />
            </div>
          )
        }
        return null
      }
      return <div key={section.id} />
    })
  }

  // Determine if name inputs are required by any corresponding signature sections
  const participantNameRequired = Array.from(nameInputEnabledSectionIds).some(id => {
    const s = sections.find(sec => sec.id === id)
    return s?.attributes?.user_type === USER_TYPES.participant
  })
  const caregiverNameRequired = hasCaregiver && Array.from(nameInputEnabledSectionIds).some(id => {
    const s = sections.find(sec => sec.id === id)
    return s?.attributes?.user_type === USER_TYPES.caregiver
  })

  // Validate that all required name inputs have values per-section
  const participantNamesValid = !participantNameRequired || (
    sections
      .filter(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.user_type === USER_TYPES.participant)
      .filter(s => nameInputEnabledSectionIds.has(s.id))
      .every(s => {
        const name = participantNames?.[s.id]
        return !!(name && name.length > 0)
      })
  )
  const caregiverNamesValid = !caregiverNameRequired || (
    sections
      .filter(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.user_type === USER_TYPES.caregiver)
      .filter(s => nameInputEnabledSectionIds.has(s.id))
      .every(s => {
        const name = caregiverNames?.[s.id]
        return !!(name && name.length > 0)
      })
  )

  // Validate that all required signatures have been entered across all sections
  const areSignaturesEntered = (userType) => {
    const signatureSections = sections.filter(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.user_type === userType)
    if (signatureSections.length === 0) return true
    return signatureSections.every(s => {
      const q = submissionConsent?.questions?.find(q => q?.id === s.id)
      if (!q) return false
      if (q?.skipped && q?.required === false) return true
      // Ensure there is at least one non-text signature entry with a value
      return Array.isArray(q?.answer) && q.answer.some(entry => entry && entry.signature_type !== CONSENT_SIGNATURE_MODES.text && entry.value)
    })
  }

  const hasParticipantSignatureSections = sections.some(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.user_type === USER_TYPES.participant)
  const hasCaregiverSignatureSections = sections.some(s => s?.type === CONSENT_SECTION_TYPES.signature && s?.attributes?.user_type === USER_TYPES.caregiver)

  const preSignatureComprehensionOk = arePreSignatureComprehensionQuestionsSatisfied(
    preSignatureComprehensionSections,
    submissionConsent?.questions
  )

  const disableConsentButton =
    !preSignatureComprehensionOk ||
    !allSignaturesComplete(submissionConsent, {
      ...(hasParticipantSignatureSections ? { participantSignatureEntered: areSignaturesEntered(USER_TYPES.participant) } : {}),
      ...(participantNameRequired ? { participantNameEntered: participantNamesValid } : {}),
      ...(hasCaregiver && hasCaregiverSignatureSections
        ? {
          caregiverSignatureEntered: areSignaturesEntered(USER_TYPES.caregiver),
          ...(caregiverNameRequired ? { caregiverNameEntered: caregiverNamesValid } : {})
        }
        : {})
    })

  const SCROLL_TOLERANCE = 2;

  const shouldShowNextButton = contentRef?.current
    ? (contentRef.current.scrollHeight - contentRef.current.clientHeight - scrollTop) > SCROLL_TOLERANCE
    : false;

  const shouldShowBackButton = scrollTop > SCROLL_TOLERANCE;

  const nextButtonDisabled = false
  const backButtonDisabled = false

  const isConsentAlreadyRead = false

  const isAwaitingAdditionalSignatures = [
    CONSENT_PARTICIPANT_STATUSES.awaitingAdmin,
    CONSENT_PARTICIPANT_STATUSES.awaitingWitness,
    CONSENT_PARTICIPANT_STATUSES.awaitingClinro
  ].includes(activeConsent?.consent_participant_status)

  return (
    <div className='consent-container'>
      <HumiLab />
      <Button
        className='logout-btn'
        onClick={() => onLogout()}
        content={strings.log_out}
        link
      />

      {changedTempPassword && !didStartConsent ? (
        <PasswordChangeSuccessView strings={strings} />
      ) : null}

      {!didStartConsent && !hasConsented && !isAwaitingAdditionalSignatures ? (
        <ConsentIntroductionView
          strings={strings}
          firstName={firstName}
          onStartConsent={onStartConsent}
          humiIsabellaDisabled={humiIsabellaDisabled}
        />
      ) : null}

      {!hasConsented && didStartConsent && !isAwaitingAdditionalSignatures && showPreSignatureComprehension && !preSignatureComprehensionComplete ? (
        <ConsentComprehension
          strings={strings}
          sections={preSignatureFlowSections}
          completeConsentQuestion={(id, data) => completeConsentQuestion(id, data)}
          downloadConsentPDF={downloadConsentPDF}
          consentName={consentName}
          consentVersion={consentVersion}
          consentId={consentId}
          isBlockingConsent={isBlockingConsent}
          onConsentClose={onConsentClose}
          onBackFromFirst={() => {
            setShowPreSignatureComprehension(false)
            setDidStartConsent(false)
          }}
          onDone={() => {
            setPreSignatureComprehensionComplete(true)
            setShowPreSignatureComprehension(false)
          }}
        />
      ) : null}

      {!hasConsented && didStartConsent && !isAwaitingAdditionalSignatures && !showComprehension && !showPreSignatureComprehension ? (
        <ConsentInProgressView
          renderContent={renderContent}
          strings={strings}
          disableConsentButton={disableConsentButton || isSubmitting}
          onConsent={onConsent}
          onConsentDecline={onConsentDecline}
          error={error}
          shouldShowNextButton={shouldShowNextButton}
          shouldShowBackButton={shouldShowBackButton}
          nextButtonDisabled={nextButtonDisabled}
          backButtonDisabled={backButtonDisabled}
          downloadConsentPDF={downloadConsentPDF}
          consentName={consentName}
          isBlockingConsent={isBlockingConsent}
          isReadOnlyConsent={readOnlyConsent}
          onConsentClose={onConsentClose}
          onConsentRead={onConsentRead}
          isConsentAlreadyRead={isConsentAlreadyRead}
          onNextButtonClick={onNextButtonClick}
          onBackButtonClick={onBackButtonClick}
          contentRef={contentRef}
          onScroll={onScroll}
          consentId={consentId}
          consentVersion={consentVersion}
          isSubmitting={isSubmitting}
        />
      ) : null}

      {!hasConsented && didStartConsent && showComprehension && !isAwaitingAdditionalSignatures && (
        <ConsentComprehension
          strings={strings}
          sections={getPostSignSections()}
          completeConsentQuestion={(id, data) => completeConsentQuestion(id, data)}
          downloadConsentPDF={downloadConsentPDF}
          consentName={consentName}
          consentVersion={consentVersion}
          consentId={consentId}
          isBlockingConsent={isBlockingConsent}
          onConsentClose={onConsentClose}
          onBackFromFirst={() => setShowComprehension(false)}
          onDone={async () => {
            // Keep comprehension view visible during submission; consent API will disable Next
            await submitWithUploads()
          }}
        />
      )}

      {hasConsented && !isAwaitingAdditionalSignatures ? (
        <ConsentCompletedView
          consentName={consentName}
          onOnboard={onOnboard}
          humiIsabellaDisabled={humiIsabellaDisabled}
          strings={strings}
        />
      ) : null}

      {isAwaitingAdditionalSignatures ? (
        <ConsentAwaitingAdditionalSignaturesView
          strings={strings}
          humiIsabellaDisabled={humiIsabellaDisabled}
          onButtonClick={() => {
            setConsentClosedToLocalStorage()
            navigateTo('/profile')
          }}
          isBlockingConsent={isBlockingConsent}
        />
      ) : null}
    </div>
  )
}

ConsentPage.propTypes = {
  changedTempPassword: PropTypes.bool,
  clearConsentStatus: PropTypes.func,
  closeModal: PropTypes.func,
  completeConsentQuestion: PropTypes.func,
  consentForm: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  downloadConsentPDF: PropTypes.func,
  endConsent: PropTypes.func,
  error: PropTypes.string,
  firstName: PropTypes.string,
  hasConsented: PropTypes.bool,
  id: PropTypes.number,
  onConsentOpen: PropTypes.func,
  onConsentReject: PropTypes.func,
  onLogout: PropTypes.func,
  onSetSubmissionConsent: PropTypes.func,
  openModal: PropTypes.func,
  strings: PropTypes.oneOfType([
    PropTypes.objectOf(PropTypes.string),
    PropTypes.object
  ]),
  studyID: PropTypes.number,
  submissionConsent: PropTypes.object,
  submitConsent: PropTypes.func,
  uploadConsentFile: PropTypes.func
}

export default ConsentPage